import React, { useContext, useRef, useState } from "react"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { ColorPicker as ColorPickerPrimitive } from "react-aria-components/ColorPicker"
import { inputSurfaceStyles } from "../input-base"
import { useFieldContext } from "../field/field-context"
import { cn, popupTransition } from "../utils"
import { StackingContext } from "../portal"
import { VisuallyHidden } from "../visually-hidden"
import { ColorInputHex } from "./color-input-hex"
import { ColorInputSwatch } from "./color-input-swatch"
import { ColorPanelPicker } from "./color-panel-picker"
import { ColorPanelSavedColors } from "./color-panel-saved-colors"
import { ColorPanelDesignColors } from "./color-panel-design-colors"
import type { SavedColors } from "./saved-colors-section"
import type { DesignColors } from "./design-colors-section"
import { useColorValue } from "./use-color-value"

// =============================================================================
// Styles
// =============================================================================

const fieldStyles = [
  "flex w-full min-w-0 items-center gap-2 py-2 pr-3 pl-2",
  ...inputSurfaceStyles,
  "has-[[data-slot=input]:disabled]:cursor-not-allowed has-[[data-slot=input]:disabled]:inset-ring-gray-200" // disabled
]

const triggerStyles = [
  "flex-none rounded",
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900",
  "disabled:cursor-not-allowed"
]

const popupStyles = [
  "w-[12.5rem] rounded border border-gray-200 bg-white p-3 shadow",
  popupTransition()
]

// =============================================================================
// Props
// =============================================================================

export type ColorInputProps = {
  /** The color: uppercase `#RRGGBB`, or "transparent" */
  value: string
  /** Called with the new color as uppercase `#RRGGBB`, or "transparent" */
  onValueChange?: (value: string) => void
  /** Offers a "transparent" tile above the hue slider */
  transparency?: boolean
  /** Saved colors for this field only, instead of the `SavedColorsProvider`'s */
  savedColors?: SavedColors
  /** Design colors for this field only, instead of the `DesignColorsProvider`'s */
  designColors?: DesignColors
  /** Names the field when it isn't inside a `Field` with a `Field.Label` */
  accessibleName?: string
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================

/**
 * A hex text box with a swatch that opens a color picker. Put it in a `Field`
 * with a `Field.Label`; it has no label of its own.
 *
 * @example
 * <Field>
 *   <Field.Label>Background color</Field.Label>
 *   <ColorInput value={color} onValueChange={setColor} transparency />
 * </Field>
 */
export function ColorInput({
  value,
  onValueChange,
  accessibleName,
  className,
  transparency,
  savedColors,
  designColors
}: ColorInputProps) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const hexRef = useRef<HTMLInputElement>(null)
  // Escape while composing with an input method only ends the composition
  const isComposingRef = useRef(false)
  const compositionEndTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [open, setOpen] = useState(false)
  // Opened by clicking the hex text: focus stays there so typing continues
  const [openedFromText, setOpenedFromText] = useState(false)
  // The Field disables the hex box on its own, but not the swatch
  const { labelId, disabled } = useFieldContext()
  const { color, handleChange } = useColorValue(value, onValueChange)
  // In the body, not the nearest portal: the picker is too tall to fit inside
  // the editor layout, which pushes it beside the field or under the layout's
  // sticky bars. Inside a dialog it stays in the dialog's portal, as `Select`
  // does, or the dialog's focus lock keeps the keyboard out of it.
  const stackingNode = useContext(StackingContext)
  const portalContainer = stackingNode?.closest("[role=dialog]")
    ? stackingNode
    : typeof document === "undefined"
      ? undefined
      : document.body

  const openFromText = () => {
    if (open || disabled) return
    setOpenedFromText(true)
    setOpen(true)
  }

  return (
    <ColorPickerPrimitive value={color} onChange={handleChange}>
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={(next, details) => {
          // Pressing or focusing the field's own text box isn't leaving it;
          // Escape and the swatch still close it
          const { reason, event } = details
          const landedOn =
            reason === "outside-press"
              ? event?.target
              : reason === "focus-out" && event instanceof FocusEvent
                ? event.relatedTarget
                : null
          if (
            !next &&
            landedOn instanceof Node &&
            anchorRef.current?.contains(landedOn)
          ) {
            return
          }
          // Only the swatch opens it through here; the text opens it directly
          if (next) setOpenedFromText(false)
          setOpen(next)
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={anchorRef}
          data-slot="color-input"
          className={cn(fieldStyles, className)}
          // A press on the padding, gaps or "#" counts as one on the hex
          // text. Prevented so focus doesn't drop to the body first.
          onMouseDown={event => {
            const target = event.target as Node
            if (
              target === hexRef.current ||
              triggerRef.current?.contains(target)
            ) {
              return
            }
            event.preventDefault()
            if (disabled) return
            hexRef.current?.focus()
            openFromText()
          }}
          // The picker listens for Escape on the document, which a container
          // like the editor's sidebar can stop short of
          onKeyDown={event => {
            if (event.key !== "Escape" || !open || isComposingRef.current) {
              return
            }
            event.stopPropagation()
            setOpen(false)
          }}
          onCompositionStart={() => {
            clearTimeout(compositionEndTimer.current)
            isComposingRef.current = true
          }}
          // Safari ends the composition before the Escape keydown, so the
          // flag clears a moment later, as in Base UI's own dismiss
          onCompositionEnd={() => {
            compositionEndTimer.current = setTimeout(() => {
              isComposingRef.current = false
            }, 5)
          }}
        >
          <PopoverPrimitive.Trigger
            ref={triggerRef}
            aria-describedby={labelId}
            disabled={disabled}
            className={cn(triggerStyles)}
          >
            <ColorInputSwatch />
            <VisuallyHidden>Choose a color</VisuallyHidden>
          </PopoverPrimitive.Trigger>
          <ColorInputHex
            ref={hexRef}
            accessibleName={accessibleName}
            onClick={openFromText}
          />
        </div>
        <PopoverPrimitive.Portal container={portalContainer}>
          <PopoverPrimitive.Positioner
            className="z-[5001]"
            anchor={anchorRef}
            side="bottom"
            align="start"
            sideOffset={8}
          >
            <PopoverPrimitive.Popup
              data-slot="color-input-popup"
              initialFocus={openedFromText ? false : undefined}
              finalFocus={openedFromText ? false : undefined}
              className={cn(popupStyles)}
            >
              <PopoverPrimitive.Title className="sr-only">
                Color picker
              </PopoverPrimitive.Title>
              <ColorPanelPicker transparency={transparency} />
              <ColorPanelSavedColors savedColors={savedColors} />
              <ColorPanelDesignColors designColors={designColors} />
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </ColorPickerPrimitive>
  )
}

ColorInput.displayName = "ColorInput"
